import { useMutation, useQuery } from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import { useScope } from "@state/scopeContext.lib";
import { workspaceMemberContract } from "@common/contracts/workspace-member.contract";
import { WorkspaceMemberDto } from "@common/dto/workspace/workspace-member.dto";
import { Workspace, WorkspaceMember } from "@common/types/workspace";
import { WorkspaceInviteDto } from "@common/dto/workspace/workspace-invite.dto";
import { InviteCode } from "@common/types/inviteCode";
import { User } from "@common/types/user";
import { WorkspacePendingInvite } from "@common/dto/workspace/workspace-pending-invite.dto";
import { UserWorkspaceRole } from "@common/userWorkspaceRoles";
import { OrganizationIdNameLogoUrlSchema } from "@common/schemas/organization.schema";
import { Organization } from "@common/types/organization";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { queryClient } from "../queryClient";
import {
    updateClientInQueryData,
    updateWorkspaceDetailsInQueryData,
    workspaceQueryKeys,
} from "./workspace.api";
import { useRefetchAllTaskQueries } from "./task.api";

const workspaceMemberContractClient = getBackendAPIClient(
    workspaceMemberContract,
);

function addInvitesToClientEntities(
    workspaceId: string,
    newInvites: Array<ReturnType<typeof WorkspaceInviteDto.deserialize>>,
) {
    updateClientInQueryData(workspaceId, (oldClient) => ({
        ...oldClient,
        entities: oldClient.entities.map((entity) => {
            const newInvitesForEntity = newInvites
                .filter((invite) =>
                    invite.entityAssignments?.some(
                        // eslint-disable-next-line sonarjs/no-nested-functions
                        (ea) => ea.entity.id === entity.id,
                    ),
                )
                .map((invite) => ({
                    id: invite.id,
                    email: invite.email as string,
                    role: invite.role as UserWorkspaceRole,
                    iconColor: invite.iconColor,
                }));

            const invites = [...(entity.invites ?? []), ...newInvitesForEntity];

            return {
                ...entity,
                invites,
            };
        }),
    }));
}

const workspaceMemberQueryKeys = {
    all: () => ["workspace-member"],
    workspace: (workspaceId: Workspace["id"]) => [
        ...workspaceMemberQueryKeys.all(),
        workspaceId,
    ],
    invites: (workspaceId: Workspace["id"]) => [
        ...workspaceMemberQueryKeys.workspace(workspaceId),
        "invites",
    ],
    member: (workspaceId: Workspace["id"], userId: User["id"]) => [
        ...workspaceMemberQueryKeys.workspace(workspaceId),
        userId,
    ],
} as const;

interface UseCurrentWorkspaceMembersOptions {
    /** `false` keeps the query idle until the members are actually shown. @default true */
    enabled?: boolean;
}

export function useCurrentWorkspaceMembers({
    enabled = true,
}: UseCurrentWorkspaceMembersOptions = {}) {
    const { workspaceId } = useScope();

    return useQuery({
        queryKey: workspaceId
            ? workspaceMemberQueryKeys.workspace(workspaceId)
            : [],
        queryFn: async () => {
            if (!workspaceId) {
                return [];
            }

            const result = await workspaceMemberContractClient.getMembers({
                params: {
                    workspaceId,
                },
            });

            return result.body.map(WorkspaceMemberDto.deserialize);
        },
        enabled: !!workspaceId && enabled,
    });
}

export function useFetchWorkspaceMember(userId: User["id"] | undefined) {
    const { workspaceId } = useScope();

    return useQuery({
        queryKey:
            workspaceId && userId !== undefined
                ? workspaceMemberQueryKeys.member(workspaceId, userId)
                : [],
        queryFn: async () => {
            if (!workspaceId || userId === undefined) {
                return null;
            }

            const result = await workspaceMemberContractClient.getMember({
                params: {
                    workspaceId,
                    memberId: userId,
                },
            });

            return WorkspaceMemberDto.deserialize(result.body);
        },
        enabled: !!workspaceId && userId !== undefined,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 30,
    });
}

export function usePendingWorkspaceInvites() {
    const { workspaceId } = useScope();

    return useQuery({
        queryKey: workspaceId
            ? workspaceMemberQueryKeys.invites(workspaceId)
            : [],
        queryFn: async () => {
            if (!workspaceId) {
                return [];
            }

            const result = await workspaceMemberContractClient.getInvites({
                params: {
                    workspaceId,
                },
            });

            return result.body.map(WorkspaceInviteDto.deserialize);
        },
        enabled: !!workspaceId,
    });
}

export function useRemoveMemberMutation(
    member: WorkspaceMember,
    workspace: Workspace,
) {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async () => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }

            await workspaceMemberContractClient.removeMember({
                params: {
                    workspaceId,
                    memberId: member.user.id,
                },
            });
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: workspaceMemberQueryKeys.workspace(workspace.id),
            });
        },
    });
}

export function useUpdateWorkspaceAssignmentMutation(
    member: WorkspaceMember,
    workspaceId: string,
) {
    return useMutation({
        mutationFn: async (
            payload: ServerInferRequest<
                typeof workspaceMemberContract.updateWorkspaceAssignment
            >["body"],
        ) => {
            await workspaceMemberContractClient.updateWorkspaceAssignment({
                params: {
                    workspaceId,
                    memberId: member.user.id,
                },
                body: payload,
            });
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: workspaceMemberQueryKeys.workspace(workspaceId),
            });
        },
    });
}

export function useInviteMemberMutation(workspaceId?: string) {
    const invalidateAllTaskQueries = useRefetchAllTaskQueries();

    return useMutation({
        mutationFn: async (
            payload: ServerInferRequest<
                typeof workspaceMemberContract.inviteMember
            >["body"],
        ) => {
            const result = await workspaceMemberContractClient.inviteMember({
                params: {
                    workspaceId: workspaceId!,
                },
                body: payload,
            });

            return result.body.map(WorkspaceInviteDto.deserialize);
        },
        onSuccess: async (newInvites) => {
            await queryClient.invalidateQueries({
                queryKey: workspaceMemberQueryKeys.workspace(workspaceId!),
            });

            await invalidateAllTaskQueries();

            const pendingInvites: WorkspacePendingInvite[] = newInvites.map(
                (invite) => ({
                    id: invite.id,
                    email: invite.email as string,
                    role: invite.role as UserWorkspaceRole,
                    iconColor: invite.iconColor,
                }),
            );

            updateWorkspaceDetailsInQueryData(
                workspaceId!,
                (workspaceDetails) => ({
                    ...workspaceDetails,
                    pendingInvites: [
                        ...workspaceDetails.pendingInvites,
                        ...pendingInvites,
                    ],
                }),
            );

            addInvitesToClientEntities(workspaceId!, newInvites);
        },
    });
}

export function useResendInviteMutation(workspace: Workspace) {
    return useMutation({
        mutationFn: async (invite: InviteCode) => {
            await workspaceMemberContractClient.resendInvite({
                params: {
                    workspaceId: workspace.id,
                    inviteId: invite.id,
                },
            });
        },
    });
}

export function useCancelInviteMutation(workspace: Workspace) {
    return useMutation({
        mutationFn: async (invite: InviteCode) => {
            await workspaceMemberContractClient.cancelInvite({
                params: {
                    workspaceId: workspace.id,
                    inviteId: invite.id,
                },
            });
        },
        onMutate: async (invite) => {
            await queryClient.setQueryData(
                workspaceMemberQueryKeys.invites(workspace.id),
                (data: WorkspaceInviteDto[]) =>
                    data.filter((i) => i.id !== invite.id),
            );
        },
        onSuccess: async (_response) => {
            await queryClient.invalidateQueries({
                queryKey: workspaceMemberQueryKeys.invites(workspace.id),
            });
        },
    });
}

export function useCheckOrganizationInvitesMutation(workspaceId?: string) {
    return useMutation({
        mutationFn: async (emails: string[]) => {
            const response =
                await workspaceMemberContractClient.checkOrganizationInvites({
                    params: {
                        workspaceId: workspaceId!,
                    },
                    body: {
                        emails,
                    },
                });

            return response.body.map((org) =>
                OrganizationIdNameLogoUrlSchema.parse(org),
            );
        },
    });
}

export function useRemoveOrganizationFromWorkspaceMutation(
    organization: Organization,
) {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async () => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }

            await workspaceMemberContractClient.removeOrganizationMembers({
                params: {
                    workspaceId,
                    organizationIdToRemove: organization.id,
                },
            });
        },
        onSuccess: async () => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }

            await queryClient.invalidateQueries({
                queryKey: workspaceMemberQueryKeys.workspace(workspaceId),
            });
        },
    });
}

export function useTransferPrimaryAdminMutation(workspaceId: string) {
    return useMutation({
        mutationFn: async (memberId: number) => {
            await workspaceMemberContractClient.transferPrimaryAdmin({
                params: { workspaceId, memberId },
            });
        },
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: workspaceMemberQueryKeys.workspace(workspaceId),
                }),
                queryClient.invalidateQueries({
                    queryKey: workspaceQueryKeys.details(workspaceId),
                }),
            ]);
        },
    });
}
